import React, { useState, useEffect } from 'react';

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Scroll spy: highlight active nav link
  useEffect(() => {
    // During static pre-render, leave activeSection empty so the captured HTML
    // matches the client's first paint (avoids a hydration mismatch on nav links).
    if (typeof window !== 'undefined' && window.__PRERENDER__) return;
    const sections = ['about-us', 'how-to-join', 'squadrons', 'join-discord'];
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-20% 0px -70% 0px' }
    );

    sections.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const navLinks = [
    { href: '/#about-us', label: 'About Us' },
    { href: '/#how-to-join', label: 'How to Join' },
    { href: '/#squadrons', label: 'Squadrons' },
    { href: '/#join-discord', label: 'Discord' },
  ];

  const linkClass = (href) => {
    const id = href.split('#')[1] || '';
    const isActive = activeSection === id;
    return `relative py-1 text-sm font-medium tracking-wide uppercase transition-colors duration-200 ${
      isActive
        ? 'text-hvy-accent'
        : 'text-gray-300 hover:text-white'
    }`;
  };

  return (
    <header
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-hvy-900/95 backdrop-blur-md shadow-lg'
          : 'bg-hvy-900/80 backdrop-blur-sm'
      }`}
    >
      <nav className="max-w-7xl mx-auto flex items-center justify-between px-6 py-3">
        {/* Desktop: Left Links */}
        <div className="hidden md:flex items-center space-x-8 flex-1">
          {navLinks.map((link) => (
            <a key={link.href} href={link.href} className={linkClass(link.href)}>
              {link.label}
            </a>
          ))}
        </div>

        {/* Center Logo */}
        <div className="flex-shrink-0">
          <a href="/">
            <img
              src="/logo.webp"
              alt="JTF Heavy"
              className={`transition-all duration-300 ${scrolled ? 'h-10' : 'h-12'}`}
            />
          </a>
        </div>

        {/* Desktop: Right Link */}
        <div className="hidden md:flex items-center justify-end flex-1">
          <a
            href="https://perscom.jointtaskforceheavy.com"
            className="text-sm font-medium tracking-wide uppercase text-gray-300 hover:text-white transition-colors duration-200"
            target="_blank"
            rel="noopener noreferrer"
          >
            PERSCOM
          </a>
        </div>

        {/* Mobile: Hamburger */}
        <button
          className="md:hidden text-gray-300 hover:text-white p-2"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle Menu"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            {menuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
      </nav>

      {/* Mobile Menu */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-300 ${
          menuOpen ? 'max-h-80 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-6 pb-4 space-y-3 border-t border-hvy-700/50">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="block text-center text-sm font-medium tracking-wide uppercase text-gray-300 hover:text-white py-2"
              onClick={() => setMenuOpen(false)}
            >
              {link.label}
            </a>
          ))}
          <a
            href="https://perscom.jointtaskforceheavy.com"
            className="block text-center text-sm font-medium tracking-wide uppercase text-gray-300 hover:text-white py-2"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setMenuOpen(false)}
          >
            PERSCOM
          </a>
        </div>
      </div>
    </header>
  );
};

export default Header;
